import { message } from "antd";
import { type Promise } from "ts-toolbelt/out/Any/Promise";

import { type BTSelectItem } from "types/apiResponse/apiResponse";

import { type APIError, showAPIErrorMessage } from "utilities/apiHandler";

import { btConfirm, btError } from "commonComponents/btWrappers/BTConfirm/BTConfirm";
import { BTFormattedPlural } from "commonComponents/btWrappers/BTFormattedPlural/BTFormattedPlural";

import { type CostType } from "handlers";

import { type ICostCodeDefaultContainer } from "entity/costCode/common/CostCode.types";
import { type CostCategoryDetailsDeleteResponse } from "entity/costCode/CostCategoryDetails/CostCategoryDetails.api.types";
import {
    type CostCodeDeleteResponse,
    type ICostCodeDeleteRequest,
} from "entity/costCode/CostCodeDetails/CostCode.api.types";

export const getDefaultCostCode = (containerData: ICostCodeDefaultContainer) => {
    if (!containerData.hasCostCodesFeature && containerData.flatRateCostCode) {
        return containerData.flatRateCostCode.value;
    } else {
        return null;
    }
};

export const costCodeDelete = async (
    costCodeId: number,
    costType: CostType,
    deleteHandler: (request: ICostCodeDeleteRequest) => Promise<CostCodeDeleteResponse>,
    beforeClose: () => void,
    handleOpenConflictModal: (itemId: number) => void,
    onCancel?: () => void,
    onDelete?: (deletedCode: BTSelectItem | null) => void,
) => {
    try {
        const deletedCostCodeRef = await deleteHandler({
            costCodeId: costCodeId,
            currentType: costType,
        });
        if (onDelete) {
            onDelete(deletedCostCodeRef.selectItem);
        }

        beforeClose();
    } catch (e: unknown) {
        const exError = e as APIError;
        const response = exError.response.data as CostCodeDeleteResponse;

        if (!response?.costCodeInUsage) {
            showAPIErrorMessage(e);
            return;
        }

        const { attachedItemCount } = response;

        // Show the CostCodeConflict window
        const content = (
            <>
                You cannot delete this code because it is attached to {attachedItemCount}{" "}
                <BTFormattedPlural value={attachedItemCount} one="item" other="items" />. You must
                remove the code from those items before deleting.
            </>
        );
        btConfirm({
            title: "",
            content: content,
            okText: "View Conflicts",
            cancelText: "Cancel",
            okType: "default",
            onOk: () => handleOpenConflictModal(costCodeId), // async () => await conflictHandler(costCodeId),
            onCancel: onCancel ? () => onCancel() : undefined,
        });
    }
};

export const costCategoryDelete = async (
    costCategoryId: number,
    deleteHandler: (id: number) => Promise<CostCategoryDetailsDeleteResponse>,
    beforeClose: () => void,
) => {
    try {
        if (costCategoryId) {
            await deleteHandler(costCategoryId);
            if (beforeClose) {
                beforeClose();
            }
            void message.success("Deleted Successfully");
        }
    } catch (e: unknown) {
        const exError = e as APIError;
        const responseData = exError.response.data as CostCategoryDetailsDeleteResponse;
        if (responseData?.hasActiveCodes) {
            showDeleteError();
        } else {
            showAPIErrorMessage(e);
        }
    }
};

const showDeleteError = () => {
    btError({
        title: "Unable To Delete Category",
        content:
            "You cannot delete this category because it has codes that are currently being used",
    });
};
